<template>
  <button
    class="base-btn"
    :class="[
      `base-btn--${type}`,
      `base-btn--${size}`,
      { 'is-disabled': disabled, 'is-loading': loading }
    ]"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <i v-if="loading" class="el-icon-loading" />
    <i v-if="icon && !loading" :class="icon" />
    <span v-if="$slots.default" :class="{ 'ml-1': icon || loading }"><slot /></span>
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    type: {
      type: String,
      default: 'default', // default, primary, danger, text, ghost
    },
    size: {
      type: String,
      default: 'medium', // small, medium, large
    },
    icon: {
      type: String,
      default: '',
    },
    loading: Boolean,
    disabled: Boolean,
  },
  methods: {
    handleClick(evt) {
      this.$emit('click', evt);
    },
  },
};
</script>

<style lang="less" scoped>
.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 1px solid transparent;
  color: var(--text-color);
  -webkit-appearance: none;
  text-align: center;
  box-sizing: border-box;
  outline: none;
  margin: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
  user-select: none;
  border-radius: 8px;

  & + & {
    margin-left: 12px;
  }

  // Sizes
  &--medium {
    padding: 10px 20px;
    font-size: 14px;
    height: 36px;
  }

  &--small {
    padding: 8px 16px;
    font-size: 12px;
    height: 32px;
  }

  &--large {
    padding: 12px 24px;
    font-size: 16px;
    height: 44px;
  }

  // Types
  &--default {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    color: var(--text-color);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
      background: rgba(255, 255, 255, 0.08);
    }
  }

  &--primary {
    background: var(--primary-color);
    border-color: var(--primary-color);
    color: white;
    box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.3);

    &:hover {
      background: linear-gradient(135deg, var(--primary-color), var(--primary-1));
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(var(--primary-color-rgb), 0.4);
    }

    &:active {
      transform: translateY(0);
      opacity: 0.9;
    }
  }

  &--danger {
    background: rgba(255, 77, 79, 0.1);
    border-color: rgba(255, 77, 79, 0.2);
    color: #ff4d4f;

    &:hover {
      background: #ff4d4f;
      border-color: #ff4d4f;
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3);
    }

    &:active {
      transform: translateY(0);
      opacity: 0.9;
    }
  }
  
  &--text {
    border-color: transparent;
    color: var(--primary-color);
    background: transparent;
    padding-left: 0;
    padding-right: 0;
    height: auto;

    &:hover {
      color: var(--primary-1);
      text-decoration: underline;
    }
  }
  
  &--ghost {
      background: transparent;
      border: 1px dashed rgba(255, 255, 255, 0.2);
      color: var(--text-secondary);
      
      &:hover {
          border-color: var(--primary-color);
          color: var(--primary-color);
          background: rgba(var(--primary-color-rgb), 0.05);
      }
  }

  // States
  &.is-disabled {
    color: var(--disabled-color);
    cursor: not-allowed;
    background-image: none;
    background-color: rgba(255, 255, 255, 0.02);
    border-color: rgba(255, 255, 255, 0.05);
    box-shadow: none;
    transform: none;
    
    &:hover {
        transform: none;
        box-shadow: none;
    }
  }

  &.is-loading {
    position: relative;
    pointer-events: none;
    
    &:before {
      pointer-events: none;
      content: '';
      position: absolute;
      left: -1px;
      top: -1px;
      right: -1px;
      bottom: -1px;
      border-radius: inherit;
      background-color: rgba(255, 255, 255, 0.35);
    }
  }
}
</style>